import type { IPredicate } from '@feathr/blackbox';
import { getDateRangeFromPreset } from '@feathr/hooks';

import type { IDonationCreatedEvent } from '../../hooks';
import type { ITransactionFilters, TDonationType } from './TransactionFilters.types';

/**
 * Translate the UI's donation-type token into the backend value used by
 * donation breadcrumbs (`donation.frequency`). The backend stores recurring
 * donations as `'monthly'`. Exhaustive switch so adding a new TDonationType
 * variant becomes a TS error here instead of a silent miscategorisation.
 */
export function donationTypeToBackend(type: TDonationType): 'one_time' | 'monthly' {
  switch (type) {
    case 'one_time':
      return 'one_time';

    case 'recurring':
      return 'monthly';
  }
}

/**
 * Build Breadcrumbs API predicates from filter state.
 * Note: donorNameSearch is NOT included - it's client-side only.
 *
 * @param filters - Current filter state
 * @returns Array of predicates to pass to Breadcrumbs.list()
 */
export function buildPredicates(filters: ITransactionFilters): IPredicate[] {
  const predicates: IPredicate[] = [];

  // Form filter
  if (filters.formIds.length > 0) {
    predicates.push({
      kind: 'activity',
      attr_against: 'form_id',
      attr_type: 'string',
      comparison: 'in',
      value: filters.formIds,
    });
  }

  // Donor filter — uses the same string/in shape as the formIds filter above.
  if (filters.donors.length > 0) {
    predicates.push({
      kind: 'activity',
      attr_against: 'per_id',
      attr_type: 'string',
      comparison: 'in',
      value: filters.donors.map((d) => d.id),
    });
  }

  // Type filter (only add if exactly one type selected)
  if (filters.donationTypes.length === 1) {
    predicates.push({
      kind: 'activity',
      attr_against: 'donation.frequency',
      attr_type: 'string',
      comparison: 'eq',
      value: donationTypeToBackend(filters.donationTypes[0]),
    });
  }

  // Timeframe filter
  if (filters.timeframe !== 'all_time') {
    const { start, end } = getDateRangeFromPreset(filters.timeframe, filters.customDateRange);
    if (start) {
      predicates.push({
        kind: 'activity',
        attr_against: 'd_c',
        attr_type: 'date',
        comparison: 'gte_date',
        value: start.toISOString(),
      });
    }
    if (end) {
      predicates.push({
        kind: 'activity',
        attr_against: 'd_c',
        attr_type: 'date',
        comparison: 'lte_date',
        value: end.toISOString(),
      });
    }
  }

  return predicates;
}

/**
 * Check if a donation event matches the current filters.
 * Used for real-time event filtering and client-side donor name search.
 *
 * @param event - The donation event to check
 * @param filters - Current filter state
 * @returns true if the event matches all active filters
 */
export function matchesFilters(
  event: IDonationCreatedEvent,
  filters: ITransactionFilters,
): boolean {
  // Form filter
  if (filters.formIds.length > 0 && !filters.formIds.includes(event.form_id)) {
    return false;
  }

  // Donor filter
  if (filters.donors.length > 0) {
    if (!event.person_id || !filters.donors.some((d) => d.id === event.person_id)) {
      return false;
    }
  }

  // Type filter
  if (filters.donationTypes.length > 0 && !filters.donationTypes.includes(event.donation_type)) {
    return false;
  }

  // Timeframe filter
  if (filters.timeframe !== 'all_time') {
    const { start, end } = getDateRangeFromPreset(filters.timeframe, filters.customDateRange);
    const eventDate = new Date(event.created_at);
    if (start && eventDate < start) {
      return false;
    }
    if (end && eventDate > end) {
      return false;
    }
  }

  return true;
}

/**
 * Count the number of active (non-default) filters.
 * Used for the "Clear (N)" button label.
 *
 * @param filters - Current filter state
 * @returns Number of active filters
 */
export function getActiveFilterCount(filters: ITransactionFilters): number {
  let count = 0;

  if (filters.formIds.length > 0) {
    count += 1;
  }
  if (filters.donors.length > 0) {
    count += 1;
  }
  if (filters.donationTypes.length > 0) {
    count += 1;
  }
  if (filters.timeframe !== 'all_time') {
    count += 1;
  }

  return count;
}
